/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * What the inbox shows before a mailbox is connected.
 *
 * Replaces the old white overlay with red "Email Configuration Required" text
 * and a single button to Profile. Not having connected a mailbox yet is not an
 * error, so this offers the providers right here; a failed settings request
 * gets InboxLoadError instead.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import colors from 'styles/tokens/colors';

/** A faded, inert inbox behind the card, so the page reads as "your inbox, once connected". */
function GhostInbox() {
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, display: 'flex', opacity: 0.45, pointerEvents: 'none' }}>
      <Box sx={{ width: 232, p: 2, borderRight: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} animation={false} />
        {[70, 55, 62, 48, 58].map((width) => (
          <Skeleton key={width} variant="text" width={`${width}%`} height={32} animation={false} />
        ))}
      </Box>
      <Box sx={{ width: { xs: '100%', md: 360 }, borderRight: 1, borderColor: 'divider' }}>
        {Array.from({ length: 7 }).map((_, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1.5, p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Skeleton variant="circular" width={36} height={36} animation={false} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="55%" animation={false} />
              <Skeleton variant="text" width="85%" animation={false} />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
    </Box>
  );
}

function ProviderButton({ provider, connecting, disabled, onConnect }) {
  const busy = connecting === provider.key;
  return (
    <Button
      variant="outlined"
      color="inherit"
      onClick={() => onConnect(provider.key)}
      disabled={disabled}
      aria-label={`Connect ${provider.label}`}
      sx={{
        justifyContent: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderColor: 'divider',
        color: 'text.primary',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box component="img" src={provider.logo} alt="" sx={{ width: 28, height: 28, flexShrink: 0 }} />
      <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" component="div" sx={{ lineHeight: 1.3 }}>
          {busy ? `Waiting for ${provider.label}...` : `Connect ${provider.label}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {provider.description}
        </Typography>
      </Box>
      {busy && <CircularProgress size={18} />}
    </Button>
  );
}

ProviderButton.propTypes = {
  provider: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
  connecting: PropTypes.string,
  disabled: PropTypes.bool,
  onConnect: PropTypes.func.isRequired,
};

ProviderButton.defaultProps = { connecting: null, disabled: false };

function InboxConnectEmptyState({ providers, providersLoading, connecting, error, onConnect, onCancel, onOpenSettings }) {
  return (
    <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <GhostInbox />
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.55)} 0%, ${alpha(theme.palette.background.default, 0.92)} 60%)`,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.08)}`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              mb: 2,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.24) : colors.brand.primaryContainer),
              color: 'primary.main',
            }}
          >
            <MarkEmailReadOutlinedIcon />
          </Box>
          <Typography variant="h6" component="h2" gutterBottom>
            Connect your mailbox
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Read, reply and send email without leaving Pearl, with your contacts and leads one search away when you compose.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {providersLoading && !providers.length ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={58} />
              <Skeleton variant="rounded" height={58} />
            </Stack>
          ) : providers.length ? (
            <Stack spacing={1.5}>
              {providers.map((provider) => (
                <ProviderButton
                  key={provider.key}
                  provider={provider}
                  connecting={connecting}
                  disabled={Boolean(connecting)}
                  onConnect={onConnect}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="info">
              Mailbox connections are not available on this workspace yet. Ask your administrator to enable Gmail or Outlook.
            </Alert>
          )}

          {connecting && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Finish signing in in the window that opened.
              </Typography>
              <Button size="small" onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 1, alignItems: 'flex-start', color: 'text.secondary' }}>
            <LockOutlinedIcon sx={{ fontSize: 16, mt: '2px' }} />
            <Typography variant="caption" color="text.secondary">
              Pearl only uses this access to show and send your email. You can disconnect at any time in{' '}
              <Link component="button" type="button" variant="caption" onClick={onOpenSettings} sx={{ verticalAlign: 'baseline' }}>
                Profile &gt; Email
              </Link>
              .
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

InboxConnectEmptyState.propTypes = {
  providers: PropTypes.arrayOf(PropTypes.object).isRequired,
  providersLoading: PropTypes.bool,
  connecting: PropTypes.string,
  error: PropTypes.string,
  onConnect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
};

InboxConnectEmptyState.defaultProps = {
  providersLoading: false,
  connecting: null,
  error: '',
};

export default InboxConnectEmptyState;
